import { Clock, Target, BadgePoundSterling, ImageIcon, CheckCircle2, AlertTriangle, Image as Img } from "lucide-react";
import { STATUS_LABEL, SLA_TONE, fmtMoney } from "./util";

export default function BottomCards({ ws }) {
  const sla = ws.sla;
  const f = ws.financials;
  const ev = ws.evidence_summary;
  const slaTone = SLA_TONE[sla.status] || SLA_TONE.GREEN;
  const status = (ws.case.status || "NEW").toUpperCase();
  const completed = ["COMPLETED", "PAID", "CLOSED"].includes(status);

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ws-bottom-cards">

      {/* Progress tracker */}
      <Card title="Progress" testid="bc-progress">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-2xl text-[#071A33] tabular-nums">{ws.progress_pct}%</p>
          <p className={`text-[10px] uppercase tracking-[0.18em] ${completed ? "text-[#064E2E]" : "text-[#475569]"}`}>
            {STATUS_LABEL[status] || status}
          </p>
        </div>
        <div className="mt-3 h-2 rounded-full bg-[#E2DCD0] overflow-hidden">
          <div
            className={`h-full transition-all ${completed ? "bg-[#064E2E]" : "bg-[#0F766E]"}`}
            style={{ width: `${ws.progress_pct}%` }}
          />
        </div>
        <p className="text-[10px] text-[#475569] mt-2">
          Stage {ws.status_flow.indexOf(status) + 1 || "?"} of {ws.status_flow.length}
        </p>
      </Card>

      {/* SLA */}
      <Card title="SLA" testid="bc-sla" border={sla.status === "RED" ? "bg-[#B91C1C]/5 border-[#B91C1C]/40" : sla.status === "AMBER" ? "bg-[#B88A2A]/5 border-[#B88A2A]/40" : "bg-white border-[#E2DCD0]"}>
        <div className="flex items-center gap-3">
          <span className={`w-3.5 h-3.5 rounded-full ${slaTone.bg}`}/>
          <p className="font-display text-xl text-[#071A33]">{slaTone.label}</p>
        </div>
        <p className="text-xs text-[#475569] mt-2">
          {sla.completed
            ? "Closed before breach."
            : sla.hours_remaining < 0
              ? `${Math.abs(sla.hours_remaining).toFixed(1)} h overdue`
              : `${sla.hours_remaining.toFixed(1)} h remaining`}
        </p>
        <p className="text-[10px] text-[#475569] mt-1">Target: {sla.target_hours} h</p>
      </Card>

      {/* Financial snapshot */}
      <Card title="Financial snapshot" testid="bc-finance">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-2xl text-[#071A33] tabular-nums">{fmtMoney(f.invoiced_value || f.quote_value)}</p>
          <span className={`text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded ${
            f.paid_status === "PAID" ? "bg-[#064E2E]/10 text-[#064E2E]" : f.paid_status === "OUTSTANDING" ? "bg-[#B88A2A]/15 text-[#B88A2A]" : "bg-[#475569]/10 text-[#475569]"
          }`}>{f.paid_status}</span>
        </div>
        <div className="text-[10px] text-[#475569] mt-2 space-y-0.5">
          <p>Quote: {fmtMoney(f.quote_value)}</p>
          <p>Customer paid: {fmtMoney(f.paid_value)}</p>
          <p>Contractor paid: {fmtMoney(f.contractor_paid)}</p>
        </div>
      </Card>

      {/* Evidence */}
      <Card title="Evidence" testid="bc-evidence">
        <div className="flex items-baseline justify-between">
          <p className="font-display text-2xl text-[#071A33] tabular-nums">{ev.total}</p>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#475569]">files</p>
        </div>
        <ul className="text-[10px] text-[#475569] mt-2 space-y-0.5">
          <li>{ev.photos} photos · {ev.videos} videos · {ev.docs} docs</li>
          {ev.total === 0 && <li className="italic">None uploaded yet.</li>}
        </ul>
      </Card>
    </div>
  );
}

function Card({ title, children, testid, border }) {
  return (
    <div className={`rounded-lg p-4 border ${border || "bg-white border-[#E2DCD0]"}`} data-testid={testid}>
      <h4 className="text-[10px] tracking-[0.22em] uppercase text-[#B88A2A] font-medium mb-2">{title}</h4>
      {children}
    </div>
  );
}
